import * as Popover from "@radix-ui/react-popover";
import {
  BuildingOfficeIcon,
  CheckIcon,
  ChevronDownIcon,
  MapPinIcon,
  TagIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { joinClassNames } from "@utils/helpers";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { useWindowSize, usePrevious } from "@utils/hooks";
import { useTranslations } from "next-intl";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/popovers/dialog";

interface FilterValue {
  Group: string;
  Key: string;
  Value: string;
  Label: string;
}

interface FacetItem {
  QKey: string;
  QValue: string;
  Label: string;
  Count: number;
}

interface Facet {
  Label: string;
  Items: FacetItem[];
}

interface Props {
  values: FilterValue[];
  options: Facet[];
  onAdd: (option: FacetItem) => void;
  onRemove: (option: FacetItem) => void;
}

function FacetIcon({ label, className }: { label: string; className: string }) {
  switch (label.toLowerCase()) {
    case "city":
      return <MapPinIcon className={className} />;
    case "venue":
      return <BuildingOfficeIcon className={className} />;
    default:
      return <TagIcon className={className} />;
  }
}

function AnimatedCount({ count }: { count: number }) {
  const previous = usePrevious(count).current;
  const direction = previous !== null && count < previous ? -1 : 1;

  return (
    <span className="relative inline-grid overflow-hidden align-middle">
      <AnimatePresence mode="popLayout" initial={false} custom={direction}>
        <motion.span
          key={count}
          custom={direction}
          initial={{ y: direction * 8, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -direction * 8, opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          className="col-start-1 row-start-1"
        >
          {count}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

function FacetFilter({
  facet,
  values,
  onAdd,
  onRemove,
}: {
  facet: Facet;
  values: FilterValue[];
  onAdd: (option: FacetItem) => void;
  onRemove: (option: FacetItem) => void;
}) {
  const windowSize = useWindowSize();
  const isMobile = !!windowSize.width && windowSize.width <= 640;
  const [open, setOpen] = useState(false);
  const t = useTranslations("Search");
  const selectedValues = values.filter((value) => value.Group === facet.Label);
  const label = t("filterby", { facet: facet.Label });

  function toggleItem(item: FacetItem) {
    if (selectedValues.map((value) => value.Label).includes(item.Label)) {
      onRemove(item);
      return;
    }
    onAdd(item);
  }

  const triggerContent = (
    <>
      <motion.span layout="position" className="flex items-center">
        <FacetIcon label={facet.Label} className="h-5 w-5" />
      </motion.span>
      <motion.span
        layout="position"
        className="font-semibold flex items-center"
      >
        {label}
        <AnimatePresence initial={false} mode="popLayout">
          {selectedValues.length > 0 ? (
            <motion.span
              key="badge"
              layout
              initial={{ opacity: 0, scale: 0.4 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.4 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="ml-1.5 rounded bg-indigo-100 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-indigo-800"
            >
              <AnimatedCount count={selectedValues.length} />
            </motion.span>
          ) : null}
        </AnimatePresence>
      </motion.span>
    </>
  );

  const items = (
    <div
      className={joinClassNames("flex flex-col", isMobile ? "gap-1" : "gap-2")}
    >
      {facet.Items.map((item) => {
        const isSelected = selectedValues
          .map((value) => value.Label)
          .includes(item.Label);

        return (
          <button
            key={`${item.QValue}-${item.Label}`}
            onClick={() => toggleItem(item)}
            className={joinClassNames(
              "flex items-center rounded-lg transition-colors ease-in-out duration-200",
              isMobile
                ? "w-full py-3 px-4 text-base"
                : "w-fit py-1 px-2 text-sm",
              isSelected
                ? "bg-indigo-100 text-indigo-800"
                : "bg-gray-100 text-gray-800"
            )}
          >
            <span
              className={joinClassNames(
                "flex items-center justify-center overflow-hidden shrink-0 transition-all duration-200 ease-in-out",
                isSelected
                  ? "w-4 mr-1.5 scale-100 opacity-100"
                  : "w-0 mr-0 scale-50 opacity-0"
              )}
            >
              <CheckIcon className="h-4 w-4" />
            </span>
            <span>
              {item.Label} ({item.Count})
            </span>
          </button>
        );
      })}
    </div>
  );

  if (windowSize.width && windowSize.width <= 640) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <motion.button
            layout
            className="flex items-center gap-1 px-1 py-0.5 text-gray-600 bg-white hover:bg-gray-100 border border-gray-300 rounded-md transition-colors ease-in-out duration-300 cursor-default w-fit focus-visible:-outline-offset-2"
          >
            {triggerContent}
            <motion.span layout="position" className="flex items-center">
              <ChevronDownIcon
                className={joinClassNames(
                  "h-5 w-5 will-change-transform transition-transform ease-in-out duration-300",
                  open ? "rotate-180" : "rotate-0"
                )}
              />
            </motion.span>
          </motion.button>
        </DialogTrigger>
        <AnimatePresence>
          {open && (
            <DialogContent
              asChild
              forceMount
              handleClose={() => setOpen(false)}
              className="bottom-4 !m-2 !p-0 !rounded-3xl !bg-white !max-w-[90dvw] max-h-[65dvh] !text-indigo-800 !border-none"
            >
              <motion.div
                initial="hidden"
                animate="show"
                exit="hidden"
                className="flex flex-col w-full"
                transition={{ duration: 0.2 }}
                variants={{
                  hidden: {
                    y: 200,
                    opacity: 0,
                  },
                  show: {
                    y: 0,
                    opacity: 1,
                  },
                }}
              >
                <div className="relative px-6 pt-6 pb-4 flex flex-col gap-3 justify-between flex-1 bg-indigo-500 rounded-t-3xl">
                  <div className="relative self-end w-8 h-8 rounded-full bg-white/10">
                    <DialogClose className="absolute top-2 left-2 w-4 h-4 text-white !font-bold">
                      <XMarkIcon />
                    </DialogClose>
                  </div>
                  <DialogHeader className="!text-left">
                    <DialogTitle className="!text-white text-xl !font-bold">
                      {label}
                    </DialogTitle>
                    <FacetIcon
                      label={facet.Label}
                      className="absolute top-0 left-4 w-24 h-24 text-white/10 -rotate-12"
                    />
                  </DialogHeader>
                </div>
                <div className="relative px-4 py-4 max-h-[calc(65dvh-116px)] overflow-y-auto text-black">
                  {items}
                </div>
              </motion.div>
            </DialogContent>
          )}
        </AnimatePresence>
      </Dialog>
    );
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <motion.button
          layout
          className="flex items-center gap-1 px-1 py-0.5 text-gray-600 bg-white hover:bg-gray-100 border border-gray-300 rounded-md transition-colors ease-in-out duration-300 cursor-default w-fit focus-visible:-outline-offset-2"
        >
          {triggerContent}
          <motion.span layout="position" className="flex items-center">
            <ChevronDownIcon
              className={joinClassNames(
                "h-5 w-5 will-change-transform transition-transform ease-in-out duration-300",
                open ? "rotate-180" : "rotate-0"
              )}
            />
          </motion.span>
        </motion.button>
      </Popover.Trigger>
      <AnimatePresence>
        {open && (
          <Popover.Portal forceMount>
            <Popover.Content
              className="relative z-40"
              sideOffset={15}
              align="end"
              alignOffset={-42}
              avoidCollisions
              collisionPadding={{ top: 90 }}
              forceMount
            >
              <motion.div
                initial="hidden"
                animate="show"
                exit="hidden"
                variants={{
                  hidden: {
                    opacity: 0,
                    y: "-1rem",
                    transition: { duration: 0.125 },
                  },
                  show: { opacity: 1, y: 0 },
                }}
                transition={{ duration: 0.25 }}
                className="relative flex flex-col rounded-2xl px-4 pb-2 bg-white text-black ring-1 ring-inset ring-gray-100 shadow min-w-[300px] max-h-96 overflow-y-auto"
              >
                <h2 className="sticky -top-px -mx-4 px-4 pt-4 pb-2 text-xl font-bold bg-white/80 backdrop-blur rounded-t-2xl">
                  {facet.Label}
                </h2>
                {items}
              </motion.div>
            </Popover.Content>
          </Popover.Portal>
        )}
      </AnimatePresence>
    </Popover.Root>
  );
}

export default function Filter({ values, options, onAdd, onRemove }: Props) {
  return (
    <motion.div layout className="flex items-center gap-2 flex-wrap">
      {options
        .filter((option) => option.Items.length > 0)
        .map((option) => (
          <FacetFilter
            key={option.Label}
            facet={option}
            values={values}
            onAdd={onAdd}
            onRemove={onRemove}
          />
        ))}
    </motion.div>
  );
}
